import { Controller } from "@hotwired/stimulus"

// Mobile-friendly event show page navigation controller
export default class extends Controller {
  static targets = ["dropdown", "dropdownButton", "dropdownMenu", "tab", "panel"]
  static values = { 
    default: String,
    current: String 
  }

  connect() {
    this.boundHandleOutsideClick = this.handleOutsideClick.bind(this)
    this.showInitialTab()
    this.updateDropdownButton()
  }

  disconnect() {
    document.removeEventListener('click', this.boundHandleOutsideClick)
  }

  // Show initial tab based on default value or first available tab
  showInitialTab() {
    const initialTab = this.resolveTab(this.defaultValue) || this.getFirstAvailableTab()
    if (initialTab) {
      this.showTab(initialTab)
      this.currentValue = initialTab
    }
  }

  // A requested tab that has no panel (an old `?tab=seating` link, for
  // example) must not leave the page blank. Seating now lives on the Guest
  // List tab, so that one lands there; anything else falls back to the
  // first tab.
  resolveTab(tabName) {
    if (!tabName) return null
    if (this.hasPanel(tabName)) return tabName
    if (tabName === 'seating' && this.hasPanel('guests')) return 'guests'
    return null
  }

  hasPanel(tabName) {
    return this.panelTargets.some(panel => panel.getAttribute('data-tab') === tabName)
  }

  // Get the first available tab from the DOM
  getFirstAvailableTab() {
    const firstTab = this.tabTargets.find(tab => tab.getAttribute('data-tab'))
    return firstTab ? firstTab.getAttribute('data-tab') : null
  }

  // Toggle dropdown menu on mobile
  toggleDropdown(event) {
    event.preventDefault()
    const isOpen = this.dropdownMenuTarget.classList.contains('show')
    
    if (isOpen) {
      this.closeDropdown()
    } else {
      this.openDropdown()
    }
  }

  // Open dropdown menu
  openDropdown() {
    this.dropdownMenuTarget.classList.remove('hidden')
    this.dropdownMenuTarget.classList.add('show')
    this.dropdownButtonTarget.setAttribute('aria-expanded', 'true')
    
    // Animate chevron
    const chevron = this.dropdownButtonTarget.querySelector('i.ph-caret-down')
    if (chevron) {
      chevron.classList.add('rotate-180')
    }
    
    // Add click outside listener
    setTimeout(() => {
      document.addEventListener('click', this.boundHandleOutsideClick)
    }, 0)
  }

  // Close dropdown menu  
  closeDropdown() {
    this.dropdownMenuTarget.classList.remove('show')
    this.dropdownMenuTarget.classList.add('hidden')
    this.dropdownButtonTarget.setAttribute('aria-expanded', 'false')
    
    // Reset chevron
    const chevron = this.dropdownButtonTarget.querySelector('i.ph-caret-down')
    if (chevron) {
      chevron.classList.remove('rotate-180')
    }
    
    // Remove click outside listener
    document.removeEventListener('click', this.boundHandleOutsideClick)
  }

  // Handle clicks outside dropdown
  handleOutsideClick(event) {
    if (!this.dropdownTarget.contains(event.target)) {
      this.closeDropdown()
    }
  }

  // Handle tab selection (both desktop and mobile)
  selectTab(event) {
    event.preventDefault()
    const tabName = event.currentTarget.getAttribute('data-tab')
    if (tabName) {
      this.showTab(tabName)
      this.currentValue = tabName
      this.updateDropdownButton()
      this.closeDropdown()

      const url = new URL(window.location)
      url.searchParams.set('tab', tabName)
      history.replaceState({}, '', url)
    }
  }

  // Show specific tab and hide others
  showTab(tabName) {
    // Hide all panels
    this.panelTargets.forEach(panel => {
      panel.classList.add('hidden')
      panel.setAttribute('aria-hidden', 'true')
    })

    // Show target panel
    const targetPanel = this.panelTargets.find(panel => 
      panel.getAttribute('data-tab') === tabName
    )

    if (targetPanel) {
      targetPanel.classList.remove('hidden')
      targetPanel.setAttribute('aria-hidden', 'false')
    }

    // Update tab states
    this.updateTabStates(tabName)
    
    // Store current tab for mobile dropdown
    this.currentValue = tabName
  }

  // Update active states for all tabs
  updateTabStates(activeTabName) {
    this.tabTargets.forEach(tab => {
      const isActive = tab.getAttribute('data-tab') === activeTabName
      
      if (isActive) {
        tab.classList.add('active')
        tab.setAttribute('aria-selected', 'true')
        tab.classList.add('bg-teal-100', 'shadow-sm', 'text-teal-500')
        tab.classList.remove('text-gray-700')
      } else {
        tab.classList.remove('active')
        tab.setAttribute('aria-selected', 'false')
        tab.classList.remove('bg-teal-100', 'shadow-sm', 'text-teal-500')
        tab.classList.add('text-gray-700')
      }
    })
  }

  // Update dropdown button text with current tab name
  updateDropdownButton() {
    if (!this.hasDropdownButtonTarget) return
    
    const currentTab = this.tabTargets.find(tab => 
      tab.getAttribute('data-tab') === this.currentValue
    )
    
    if (currentTab) {
      const tabText = this.extractTabText(currentTab)
      this.dropdownButtonTarget.querySelector('.dropdown-text').textContent = tabText
    }
  }

  // Extract clean text from tab button (removing icons, counts, etc.)
  extractTabText(tabElement) {
    const textElement = tabElement.querySelector('.tab-text')
    if (textElement) {
      return textElement.textContent.trim()
    }
    
    // Fallback: get text content and clean it
    let text = tabElement.textContent.trim()
    // Remove numbers in parentheses or badges
    text = text.replace(/\(\d+\)|\d+$/, '').trim()
    return text
  }

  // Handle keyboard navigation
  handleKeydown(event) {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return
    
    event.preventDefault()
    
    const availableTabs = this.tabTargets.filter(tab => 
      tab.getAttribute('data-tab')
    )
    
    const currentIndex = availableTabs.findIndex(tab => 
      tab.getAttribute('data-tab') === this.currentValue
    )
    
    let newIndex
    switch (event.key) {
      case 'ArrowLeft':
        newIndex = currentIndex > 0 ? currentIndex - 1 : availableTabs.length - 1
        break
      case 'ArrowRight':
        newIndex = currentIndex < availableTabs.length - 1 ? currentIndex + 1 : 0
        break
      case 'Home':
        newIndex = 0
        break
      case 'End':
        newIndex = availableTabs.length - 1
        break
    }
    
    if (newIndex !== undefined) {
      const newTab = availableTabs[newIndex]
      const tabName = newTab.getAttribute('data-tab')
      this.showTab(tabName)
      this.updateDropdownButton()
      newTab.focus()
    }
  }
}